/**
 * draftingContentStream - binary streaming helper for
 *   POST /drafting/drafts/<uuid>/stream
 *   POST /drafting/insights/<uuid>/stream
 *
 * For standalone drafts/strategies the response body is UTF-8 markdown.
 * For matter-mode drafts the response body is a DOCX binary.
 *
 * Dispatches synchronous slice actions directly so the component receives
 * progressive content updates without going through a thunk. The caller
 * creates an AbortController and passes its signal; cleanup calls
 * controller.abort() which surfaces as an AbortError and is swallowed.
 */

import { config } from '../../utils/config';
import { tokenProvider } from '../../utils/apis';
import type { AppDispatch } from '../../store';
import type { DraftingKind } from '../../store/slices/draftingSlice';
import {
  streamingStarted,
  streamingAppendChunk,
  streamingDone,
  streamingFailed,
} from '../../store/slices/draftingSlice';

/** Reads all chunks from the stream body, dispatching chunk actions along the way. */
async function consumeStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  kind: DraftingKind,
  uuid: string,
  isText: boolean,
  dispatch: AppDispatch,
): Promise<BlobPart[]> {
  const decoder = isText ? new TextDecoder('utf-8') : null;
  const blobChunks: BlobPart[] = [];
  let chunk = await reader.read();
  while (!chunk.done) {
    if (isText && decoder) {
      dispatch(streamingAppendChunk({ kind, uuid, text: decoder.decode(chunk.value, { stream: true }) }));
    } else {
      const safeChunk = chunk.value.slice(); // Uint8Array<ArrayBuffer> - safe for Blob
      blobChunks.push(safeChunk);
      dispatch(streamingAppendChunk({ kind, uuid, blobChunk: new Blob([safeChunk]) }));
    }
    chunk = await reader.read(); // eslint-disable-line no-await-in-loop
  }
  return blobChunks;
}

export async function openDraftingContentStream({
  kind,
  uuid,
  dispatch,
  signal,
}: {
  kind: DraftingKind;
  uuid: string;
  dispatch: AppDispatch;
  signal: AbortSignal;
}): Promise<void> {
  dispatch(streamingStarted({ kind, uuid }));
  try {
    const token = tokenProvider ? await tokenProvider() : null;
    const path = kind === 'draft'
      ? `/drafting/drafts/${uuid}/stream`
      : `/drafting/insights/${uuid}/stream`;

    const res = await fetch(`${config.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ disposition: 'inline' }),
      credentials: 'include',
      signal,
    });

    if (!res.ok || !res.body) {
      throw new Error(`Stream failed: ${res.status}`);
    }

    // The server tells us what it sent via Content-Type - we honor that instead
    // of guessing from matter binding. Markdown/text is decoded and streamed as
    // editable text; anything else (DOCX for legacy matter documents without a
    // markdown source) is accumulated as a binary blob and rendered via the DOCX
    // viewer. Both document modes now travel one identical client path.
    const contentType = res.headers.get('content-type') ?? 'application/octet-stream';
    const isText = /^text\/|markdown|json/i.test(contentType);

    const blobChunks = await consumeStream(res.body.getReader(), kind, uuid, isText, dispatch);

    if (isText) {
      dispatch(streamingDone({ kind, uuid }));
    } else {
      dispatch(
        streamingDone({ kind, uuid, finalBlob: new Blob(blobChunks, { type: contentType }) }),
      );
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') return; // expected on unmount / navigation
    dispatch(streamingFailed({ kind, uuid, error: e?.message ?? 'Stream failed' }));
  }
}
